import {
  IAddToMarketListButtonVariant,
  IAddToMarketListButton,
} from "@faire/deprecated-retailer-visitor-shared/components/Events/AddToMarketListButton/AddToMarketListButton.props";
import { useEventsStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/shared/EventsStore";
import { Core } from "@faire/design-tokens";
import { LoadingSpinner } from "@faire/slate/components/loading-spinner";
import { CheckOutline } from "@faire/slate/icons/CheckOutline";
import { Subtract } from "@faire/slate/icons/Subtract";
import { isTouchDevice } from "@faire/web/common/isTouchDevice";
import { Button } from "@faire/web/slate-deprecated/Button/index";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";
import { EventsThemeShape } from "@faire/web/slate/Theme/EventsThemeShape";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useState } from "@faire/web/ui/hooks/useState";
import React, { useMemo } from "react";
import styled, { css } from "styled-components";

export const AddToMarketListButtonInternal: React.FC<
  IAddToMarketListButton
> = ({
  variant,
  className,
  onClick,
  isLoading,
  addText,
  addedText,
  removeText,
  showIconOnRight,
}) => {
  const { isMarketEvent } = useEventsStore();
  const [isHovering, setIsHovering] = useState<boolean>(false);

  const text = useMemo(() => {
    if (variant === IAddToMarketListButtonVariant.ADDED) {
      const nonHoveringText = addedText ?? addText;
      return isHovering ? removeText : nonHoveringText;
    }
    return addText;
  }, [addText, addedText, removeText, variant, isHovering]);

  const buttonStyle = getButtonStyle(isMarketEvent);

  return (
    <StyledButton
      data-test-id="add-to-market-list-button"
      variant={
        variant === IAddToMarketListButtonVariant.ADDED
          ? "secondary"
          : "primary"
      }
      $added={variant === IAddToMarketListButtonVariant.ADDED}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      disabled={isLoading}
      onMouseEnter={!isTouchDevice() ? () => setIsHovering(true) : undefined}
      onMouseLeave={() => setIsHovering(false)}
      $buttonStyle={buttonStyle}
    >
      <GridWrapper>
        <RowWrapper $show>
          {showIconOnRight ? (
            <Row align="center" justify="center">
              <WrappedTypography variant="paragraphSansRegular">
                {text}
              </WrappedTypography>
              <Spacer width="1x" />
              <ButtonIcon
                isHovering={isHovering}
                variant={isLoading ? "loading" : variant}
                increaseIconStroke
                isMarketEvent={isMarketEvent}
              />
            </Row>
          ) : (
            <Row align="center">
              <ButtonIcon
                isHovering={isHovering}
                variant={isLoading ? "loading" : variant}
                isMarketEvent={isMarketEvent}
              />
              <Spacer width="1x" />
              <WrappedTypography variant="paragraphSansRegular">
                {text}
              </WrappedTypography>
            </Row>
          )}
        </RowWrapper>
        {text !== addText
          ? [addedText, removeText].map((text) => (
              <RowWrapper key={text}>
                <Row align="center">
                  <ButtonIcon
                    isHovering={isHovering}
                    variant={isLoading ? "loading" : variant}
                    isMarketEvent={isMarketEvent}
                  />
                  <Spacer width="1x" />
                  <WrappedTypography variant="paragraphSansMedium">
                    {text}
                  </WrappedTypography>
                </Row>
              </RowWrapper>
            ))
          : null}
      </GridWrapper>
    </StyledButton>
  );
};

const WrappedTypography = styled(Typography)`
  min-width: 70px;
  line-height: 16px;
`;

enum ButtonStyles {
  MarketEvent,
  DefaultStyle,
}

const getButtonStyle = (isMarketEvent?: boolean) => {
  if (isMarketEvent) {
    return ButtonStyles.MarketEvent;
  }
  return ButtonStyles.DefaultStyle;
};

const ButtonIcon: React.FC<{
  isHovering: boolean;
  variant: IAddToMarketListButtonVariant | "loading";
  increaseIconStroke?: boolean;
  isMarketEvent?: boolean;
}> = ({ isHovering, variant, increaseIconStroke, isMarketEvent }) => {
  const defaultSvgProps = { color: getEventTheme("faireMarket").promoText };
  const svgProps = increaseIconStroke
    ? { strokeWidth: 2, ...defaultSvgProps }
    : defaultSvgProps;
  if (variant === IAddToMarketListButtonVariant.ADDED) {
    return isHovering ? (
      <Subtract {...svgProps} />
    ) : (
      <CheckOutline {...svgProps} width="16px" height="16px" />
    );
  } else if (variant === "loading") {
    return <LoadingSpinner data-test-id="loading-spinner" size="small" />;
  } else {
    return <MarketBadge $isMarketEvent={isMarketEvent} {...svgProps} />;
  }
};

const styles = (buttonStyle: ButtonStyles) => {
  switch (buttonStyle) {
    case ButtonStyles.MarketEvent:
      return {
        hoverRemove: css`
          background-color: ${Core.text.inverse};
          color: ${getEventTheme("faireMarket").primaryLight};
          border: 1px solid ${Core.border.default};
          & ${WrappedTypography} {
            color: ${getEventTheme("faireMarket").promoText};
          }
          & svg {
            color: ${Core.text.hover} !important;
          }
        `,
        hoverNotAdded: css`
          background-color: ${Core.text.inverse};
          color: ${getEventTheme("faireMarket").primaryLight};
          border: 1px solid ${Core.border.default};
          & ${WrappedTypography} {
            color: ${getEventTheme("faireMarket").primaryLight};
          }
        `,
        added: css`
          color: ${Core.text.inverse};
          background-color: ${Core.surface.inverse};
          border: 1px solid ${Core.border.muted};
          & ${WrappedTypography} {
            color: ${getEventTheme("faireMarket").promoText};
          }
          & svg {
            color: ${getEventTheme("faireMarket").badgeIcon} !important;
          }
        `,
        default: css`
          color: ${getEventTheme("faireMarket").primaryLight};
          background-color: ${Core.surface.inverse};
          border: 1px solid ${Core.border.muted};
          & ${WrappedTypography} {
            color: ${getEventTheme("faireMarket").promoText} !important;
          }
          & svg {
            width: 16px;
          }
        `,
        disabled: css`
          color: ${Core.text.inverse};
          background-color: ${Core.surface.disabled};
          border: 1px solid ${Core.border.default};
          & ${WrappedTypography} {
            color: ${Core.text.default};
          }
        `,
      };
    default:
      return {
        hoverRemove: css`
          background-color: ${getEventTheme("faireMarket").primaryDark};
          color: ${Core.text.inverse};
          border: 1px solid ${getEventTheme("faireMarket").primaryDark};
          & ${WrappedTypography} {
            color: ${Core.text.inverse};
          }
        `,
        hoverNotAdded: css`
          background-color: ${getEventTheme("faireMarket").primaryDark};
          color: ${Core.text.inverse};
          border: 1px solid ${getEventTheme("faireMarket").primaryDark};
          & ${WrappedTypography} {
            color: ${Core.text.inverse};
          }
        `,
        added: css`
          color: ${Core.text.inverse};
          background-color: ${Core.text.default};
          border: 1px solid ${Core.border.default};
          & ${WrappedTypography} {
            color: ${Core.text.inverse};
          }
        `,
        default: css`
          color: ${Core.text.default};
          background-color: ${Core.surface.inverse};
          border: 1px solid ${Core.border.default};
          & ${WrappedTypography} {
            color: ${Core.text.default};
          }
          & svg {
            width: 16px;
          }
        `,
        disabled: css`
          color: ${Core.text.inverse};
          background-color: ${Core.surface.disabled};
          border: 1px solid ${Core.border.default};
          & ${WrappedTypography} {
            color: ${Core.text.default};
          }
        `,
      };
  }
};

const StyledButton = styled(Button)<{
  $added: boolean;
  disabled?: boolean;
  $buttonStyle: ButtonStyles;
}>`
  border-radius: ${getSpacing("4x")};
  min-width: 136px;
  min-height: 40px;
  top: -20px;
  height: fit-content;
  padding: ${getSpacing(8, 20, 8, 20)};
  border: 1px solid ${getEventTheme("faireMarket").primaryDark};
  font-weight: 500;

  ${(props) =>
    props.$added
      ? styles(props.$buttonStyle).added
      : styles(props.$buttonStyle).default}
  &:active,
  &:focus,
  &:hover {
    // for touch devices
    ${(props) =>
      props.$added
        ? styles(props.$buttonStyle).added
        : styles(props.$buttonStyle).default}
  }
  ${responsiveMediaQueries.canHover} {
    &:hover {
      ${(props) =>
        props.$added
          ? styles(props.$buttonStyle).hoverRemove
          : styles(props.$buttonStyle).hoverNotAdded};
    }
  }
  ${(props) => (props.disabled ? styles(props.$buttonStyle).disabled : "")}
`;

const GridWrapper = styled.div`
  display: grid;
`;

interface IRowWrapper {
  $show?: boolean;
}
const RowWrapper = styled.div<IRowWrapper>`
  grid-column: 1;
  grid-row: 1;
  justify-self: center;
  align-self: center;
  visibility: ${(props) => (props.$show ? "visible" : "hidden")};
  height: ${(props) => (props.$show ? "auto" : 0)};
`;

const MarketBadge = styled(EventsThemeShape.faireMarket.badge).attrs<{
  $isMarketEvent?: boolean;
}>(({ $isMarketEvent }) => ({
  color: $isMarketEvent
    ? getEventTheme("faireMarket").badgeIcon
    : Core.text.inverse,
}))<{ $isMarketEvent?: boolean }>``;
